import { invalidateTournamentManagementSummary } from './tournamentsTable';
import axios from 'Src/helpers/api/axiosInstance';
import { AppUser } from 'Src/views/App/App';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from '../../../consts/table';
import { DEFAULT_FILTER } from '../admin/user';
import { ConfirmationRequestTemplate } from '../../../models/confirmationRequestTemplate';
import { FormDefinition } from '../../../types/formSchema';
import { Count } from '../../../models/count';

export function getTournamentParticipants(user: AppUser, tournamentId: string, filter = DEFAULT_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter
    }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/participants`, config)
    .then(response => {
      return response.data;
    });
}

export function getTournamentParticipantsCount(user: AppUser, tournamentId: string, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: where
        }
      }
    };
  } else {
    config = {
      headers: { usid: key }
    };
  }

  return axios
    .get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/participants/count`, config)
    .then(response => {
      return response.data;
    });
}

export function getAllTournamentParticipants(user: AppUser, tournamentId: string, where?) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        }
      }
    };
  }

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/participants`, config)
    .then(response => {
      return response.data;
    });
}

export function removeTournamentParticipant(user: AppUser, tournamentId: string, participantId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(
      `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/participants/${participantId}`,
      config
    )
    .then(response => {
      invalidateTournamentManagementSummary(user, tournamentId);
      return response.data;
    });
}

export function moveTournamentParticipantsToWaitingList(user: AppUser, tournamentId: string, schoolIds: string[]) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/participants/moveToWaitingList`,
      { schoolIds },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function moveTournamentWaitingListToParticipants(
  user: AppUser,
  tournamentId: string,
  schoolIds: string[],
  eventIds?: string[]
) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/waitingList/moveToParticipants`,
      { schoolIds, eventIds },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function addTournamentParticipants(user: AppUser, tournamentId: string, data) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/participants/batch`, data, config)
    .then(response => {
      invalidateTournamentManagementSummary(user, tournamentId);
      return response.data;
    });
}

export function getConfirmationRequestTemplate(user: AppUser): Promise<ConfirmationRequestTemplate> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .get<ConfirmationRequestTemplate>(
      `${window.apiBase}/i/schools/${activeSchoolId}/confirmationRequestTemplate`,
      config
    )
    .then(response => {
      return response.data;
    });
}

export function updateConfirmationRequestTemplate(
  user: AppUser,
  data: {
    fields: any[];
    schemaVersion?: 1 | 2;
    formDefinition?: FormDefinition;
  }
) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/confirmationRequestTemplate`, data, config)
    .then(response => {
      return response.data;
    });
}

export function sendConfirmationRequest(user: AppUser, tournamentId: string, data) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/messages/confirmationRequest`,
      data,
      config
    )
    .then(response => {
      return response.data;
    });
}
